/**
 * Los documentos legales, sus direcciones y su orden.
 *
 * Cada documento tiene una dirección en español y otra en inglés, y las dos
 * abren el mismo documento: el idioma en que se lee lo decide quien mira, no la
 * dirección. Las direcciones que ya existían (`/terminos`, `/privacidad`,
 * `/terms`, `/privacy` y `/legal`) siguen funcionando, porque hay enlaces
 * publicados —también en la verificación de Google— que apuntan a ellas.
 */
import type { LegalDocPair, LegalSlug } from './types'
import { LEGAL_PATHS, type LegalLanguage } from './paths'
import { TERMS } from './docs/terms'
import { PRIVACY } from './docs/privacy'
import { AUP } from './docs/aup'
import { BETA } from './docs/beta'
import { BILLING } from './docs/billing'
import { SELLERS } from './docs/sellers'
import { DOMAINS } from './docs/domains'
import { AI } from './docs/ai'
import { COPYRIGHT } from './docs/copyright'
import { COOKIES } from './docs/cookies'
import { DPA } from './docs/dpa'
import { SUBPROCESSORS } from './docs/subprocessors'

export type { LegalLanguage }

export const LEGAL_DOCS: Record<LegalSlug, { docs: LegalDocPair; paths: Record<LegalLanguage, string> }> = {
  terms: { docs: TERMS, paths: LEGAL_PATHS.terms },
  privacy: { docs: PRIVACY, paths: LEGAL_PATHS.privacy },
  aup: { docs: AUP, paths: LEGAL_PATHS.aup },
  cookies: { docs: COOKIES, paths: LEGAL_PATHS.cookies },
  copyright: { docs: COPYRIGHT, paths: LEGAL_PATHS.copyright },
  billing: { docs: BILLING, paths: LEGAL_PATHS.billing },
  sellers: { docs: SELLERS, paths: LEGAL_PATHS.sellers },
  domains: { docs: DOMAINS, paths: LEGAL_PATHS.domains },
  ai: { docs: AI, paths: LEGAL_PATHS.ai },
  beta: { docs: BETA, paths: LEGAL_PATHS.beta },
  dpa: { docs: DPA, paths: LEGAL_PATHS.dpa },
  subprocessors: { docs: SUBPROCESSORS, paths: LEGAL_PATHS.subprocessors },
}

/** Cómo se agrupan en la página de inicio de lo legal. */
export const LEGAL_GROUPS: { key: 'core' | 'product' | 'data'; slugs: LegalSlug[] }[] = [
  { key: 'core', slugs: ['terms', 'privacy', 'aup', 'cookies', 'copyright'] },
  { key: 'product', slugs: ['billing', 'sellers', 'domains', 'ai', 'beta'] },
  { key: 'data', slugs: ['dpa', 'subprocessors'] },
]

export const LEGAL_SLUGS = Object.keys(LEGAL_DOCS) as LegalSlug[]

/** La dirección de un documento en un idioma. */
export function legalPathFor(slug: LegalSlug, language: LegalLanguage): string {
  return LEGAL_DOCS[slug].paths[language]
}

/** Lo que se muestra en una dirección: un documento, el índice, o nada. */
export type LegalRoute = { kind: 'doc'; slug: LegalSlug } | { kind: 'index' }

export function legalRouteFromPath(pathname: string): LegalRoute | null {
  const clean = pathname.replace(/\/+$/, '') || '/'
  if (clean === '/legal') return { kind: 'index' }
  for (const slug of LEGAL_SLUGS) {
    const { paths } = LEGAL_DOCS[slug]
    if (clean === paths.es || clean === paths.en) return { kind: 'doc', slug }
  }
  return null
}

/**
 * Los enlaces viejos con ancla, `/legal#terminos`, `/legal#privacidad` y
 * `/legal#cookies`, ya no encuentran su sección en la página de inicio: ahora
 * cada cosa tiene su dirección. Se llevan al documento que correspondía.
 */
export function legacyLegalTarget(hash: string): LegalSlug | null {
  if (hash === '#terminos') return 'terms'
  if (hash === '#privacidad') return 'privacy'
  if (hash === '#cookies') return 'cookies'
  return null
}
